import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChapterMarker, RevealText } from './shared';

gsap.registerPlugin(ScrollTrigger);

// Bomber jacket front-view sketch, centered in viewBox 0 0 500 310
const BOMBER = [
  // torso
  'M185 92 Q185 78 198 72 L228 58 Q250 50 272 58 L302 72 Q315 78 315 92 L315 204 L185 204 Z',
  // collar rib band
  'M228 58 Q250 46 272 58 L268 71 Q250 61 232 71 Z',
  // left sleeve
  'M185 94 L152 108 Q144 112 142 122 L132 186 Q130 196 140 198 L164 202 Q172 203 174 194 L184 132',
  // right sleeve
  'M315 94 L348 108 Q356 112 358 122 L368 186 Q370 196 360 198 L336 202 Q328 203 326 194 L316 132',
  // hem band
  'M185 204 L315 204 L315 218 L185 218 Z',
  // shoulder seams
  'M198 72 L232 90', 'M302 72 L268 90',
  // pockets
  'M206 158 L232 166', 'M294 158 L268 166',
];

const BOMBER_DETAILS = [
  // zipper
  { d: 'M250 64 L250 204', dash: '3 4' },
  // hem ribs
  ...Array.from({ length: 12 }, (_, i) => ({ d: `M${195 + i * 10} 206 L${195 + i * 10} 216`, dash: '' })),
  // collar ribs
  { d: 'M238 55 L240 66', dash: '' }, { d: 'M250 51 L250 63', dash: '' }, { d: 'M262 55 L260 66', dash: '' },
  // cuff ribs
  { d: 'M138 189 L146 190', dash: '' }, { d: 'M137 194 L158 197', dash: '' },
  { d: 'M362 189 L354 190', dash: '' }, { d: 'M363 194 L342 197', dash: '' },
];

const SIDE_PIECES = [
  { d: 'M40 80 L95 70 Q104 69 105 78 L110 168 L48 178 Z', label: 'SLEEVE ×2', lx: 44, ly: 198 },
  { d: 'M400 70 L465 70 L472 178 L395 178 Z', label: 'BACK PANEL', lx: 398, ly: 198 },
  { d: 'M40 236 L130 236 L130 258 L40 258 Z', label: 'COLLAR RIB', lx: 40, ly: 278 },
  { d: 'M395 236 L470 236 L470 254 L395 254 Z', label: 'POCKET WELT ×2', lx: 380, ly: 274 },
];

const ALL_OUTLINES = [...BOMBER, ...SIDE_PIECES.map((p) => p.d)];

function buildBlueprintMarkup() {
  const ghost = ALL_OUTLINES.map((d) => `<path d="${d}" stroke="#B89B72" stroke-opacity="0.45" stroke-width="1" fill="none"/>`).join('');
  const details = BOMBER_DETAILS.map((p) => `<path d="${p.d}" stroke="#B89B72" stroke-opacity="0.35" stroke-width="0.8" fill="none"${p.dash ? ` stroke-dasharray="${p.dash}"` : ''}/>`).join('');
  const draw = ALL_OUTLINES.map((d) => `<path class="pattern-piece" d="${d}" stroke="#D4AF37" stroke-width="1.3" fill="none"/>`).join('');
  const labels = SIDE_PIECES.map((p) => `<text class="pattern-label" x="${p.lx}" y="${p.ly}" fill="#A3A3A3" font-size="10" letter-spacing="2" font-family="Manrope, sans-serif" opacity="0">${p.label}</text>`).join('');
  return `${ghost}${details}${draw}${labels}
    <text class="pattern-label" x="212" y="248" fill="#A3A3A3" font-size="10" letter-spacing="2" font-family="Manrope, sans-serif" opacity="0">FRONT — BOMBER</text>
    <text x="30" y="303" fill="#525252" font-size="8" letter-spacing="3" font-family="Manrope, sans-serif">PAKKAR TANVEER EXPORTS — MASTER PATTERN — SUEDE BOMBER</text>`;
}

export default function Ch3Pattern() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      document.querySelectorAll('.pattern-piece').forEach((p) => {
        const len = p.getTotalLength();
        p.style.strokeDasharray = `${len}`;
        p.style.strokeDashoffset = `${len}`;
      });
      gsap.to('.pattern-piece', {
        strokeDashoffset: 0,
        stagger: 0.08,
        ease: 'none',
        scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', end: 'center 35%', scrub: 1 },
      });
      gsap.fromTo(
        '.pattern-label',
        { opacity: 0 },
        {
          opacity: 1,
          stagger: 0.12,
          scrollTrigger: { trigger: sectionRef.current, start: 'top 45%', end: 'center 30%', scrub: 1 },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="ch-3" ref={sectionRef} data-testid="chapter-3" className="relative min-h-screen bg-[#0d0b09] blueprint-grid px-6 md:px-16 py-28 md:py-40">
      <ChapterMarker num="03" title="Pattern Making" />
      <div className="md:grid md:grid-cols-12 md:gap-12 items-start">
        <div className="md:col-span-4">
          <RevealText className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
            The bomber,<br /><span className="italic text-[#B89B72]">deconstructed.</span>
          </RevealText>
          <p className="font-body font-light text-sm text-[#A3A3A3] max-w-xs mt-8 leading-relaxed">
            Before the first cut, the master pattern of our suede bomber is drafted —
            fourteen pieces, drawn to the millimetre. Scroll, and the blueprint traces itself.
          </p>
          <div className="mt-10 space-y-2 font-accent text-[9px] text-[#525252]">
            <div>SCALE — 1 : 4</div>
            <div>TOLERANCE — ± 0.5 MM</div>
            <div>DRAFT NO. — PT-1984</div>
          </div>
        </div>
        <div className="md:col-span-8 mt-14 md:mt-0 border border-[#2A2A2A] bg-[#0A0A0A]/50 backdrop-blur-sm p-4 md:p-8">
          {/* Rendered as raw markup: mapped JSX children inside <svg> get wrapped in spans by build tooling, which breaks SVG rendering */}
          <svg viewBox="0 0 500 310" className="w-full h-auto" fill="none" dangerouslySetInnerHTML={{ __html: buildBlueprintMarkup() }} />
        </div>
      </div>
    </section>
  );
}
